/**
 * BRXCheckoutReturn
 *
 * Renders the checkout-return product card and wires its CTAs.
 * Shows the selected product's image, title, description, and
 * treatment details accordion alongside a Checkout button.
 *
 * Usage:
 *   window.BRXCheckoutReturn.mount(sectionRoot, options);
 */
(function () {
    var usageMap = {
        as_needed: {
            bestForLabel:      'Best for',
            bestForValue:      'In the moment',
            durationLabel:     'Supports you',
            durationValue:     'Up to 36 hours',
            instructionsLabel: 'Take',
            instructionsValue: '15 min before sex'
        },
        daily: {
            bestForLabel:      'Best for',
            bestForValue:      'Always ready',
            durationLabel:     'Supports you',
            durationValue:     '24/7 support',
            instructionsLabel: 'Take',
            instructionsValue: 'Once daily'
        }
    };

    function escapeHtml(value) {
        return String(value == null ? '' : value)
            .replace(/&/g,  '&amp;')
            .replace(/</g,  '&lt;')
            .replace(/>/g,  '&gt;')
            .replace(/"/g,  '&quot;')
            .replace(/'/g,  '&#39;');
    }

    function renderUsageChips(usageType, vertical) {
        var usage = usageMap[usageType];
        if (!usage) return '';

        function chip(label, value) {
            return '<div class="tw-flex tw-flex-col tw-items-center tw-gap-1 tw-rounded-lg tw-border tw-border-black/10 tw-px-3 tw-py-2 md:tw-py-3 tw-text-center">' +
                '<span class="tw-text-xs md:tw-text-sm tw-leading-none tw-text-charcoal/60 tw-font-medium">'                  + escapeHtml(label) + '</span>' +
                '<span class="tw-text-sm md:tw-text-base tw-font-medium tw-leading-none md:tw-leading-none tw-text-charcoal">' + escapeHtml(value) + '</span>' +
                '</div>';
        }

        var containerClass = vertical ? 'tw-flex tw-flex-col tw-gap-2' : 'tw-grid tw-grid-cols-3 tw-gap-2';

        return '<div class="' + containerClass + '">' +
            chip(usage.bestForLabel,      usage.bestForValue) +
            chip(usage.durationLabel,     usage.durationValue) +
            chip(usage.instructionsLabel, usage.instructionsValue) +
            '</div>';
    }

    function renderAccordionItem(title, contentHtml) {
        if (!contentHtml) return '';

        return '<div class="tw-border-b tw-border-black/10" data-accordion-item>' +
            '<button type="button" class="tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-4 tw-py-4 tw-text-left" data-accordion-trigger aria-expanded="false">' +
                '<span class="tw-text-base tw-font-medium tw-text-charcoal">' + escapeHtml(title) + '</span>' +
                '<span class="material-symbols-outlined tw-text-[20px] tw-text-charcoal tw-transition-transform tw-duration-200" data-accordion-icon aria-hidden="true">expand_more</span>' +
            '</button>' +
            '<div class="tw-grid tw-grid-rows-[0fr] tw-transition-[grid-template-rows] tw-duration-200 tw-ease-out" data-accordion-panel aria-hidden="true">' +
                '<div class="tw-overflow-hidden">' +
                    '<div class="tw-pb-4 tw-text-sm tw-leading-relaxed tw-text-charcoal/80">' + contentHtml + '</div>' +
                '</div>' +
            '</div>' +
        '</div>';
    }

    function initAccordions(container) {
        var accordions = container.querySelectorAll('[data-brx-return-accordion]');

        accordions.forEach(function (accordion) {
            if (accordion.dataset.initialized === 'true') return;
            accordion.dataset.initialized = 'true';

            var items = accordion.querySelectorAll('[data-accordion-item]');

            function closeItem(item) {
                var trigger = item.querySelector('[data-accordion-trigger]');
                var panel   = item.querySelector('[data-accordion-panel]');
                var icon    = item.querySelector('[data-accordion-icon]');
                if (!trigger || !panel) return;
                trigger.setAttribute('aria-expanded', 'false');
                panel.setAttribute('aria-hidden', 'true');
                panel.classList.remove('tw-grid-rows-[1fr]');
                panel.classList.add('tw-grid-rows-[0fr]');
                if (icon) icon.classList.remove('tw-rotate-180');
            }

            function openItem(item) {
                var trigger = item.querySelector('[data-accordion-trigger]');
                var panel   = item.querySelector('[data-accordion-panel]');
                var icon    = item.querySelector('[data-accordion-icon]');
                if (!trigger || !panel) return;
                trigger.setAttribute('aria-expanded', 'true');
                panel.setAttribute('aria-hidden', 'false');
                panel.classList.remove('tw-grid-rows-[0fr]');
                panel.classList.add('tw-grid-rows-[1fr]');
                if (icon) icon.classList.add('tw-rotate-180');
            }

            items.forEach(function (item) {
                var trigger = item.querySelector('[data-accordion-trigger]');
                if (!trigger) return;
                trigger.addEventListener('click', function () {
                    var isOpen = trigger.getAttribute('aria-expanded') === 'true';
                    items.forEach(function (other) { if (other !== item) closeItem(other); });
                    if (isOpen) closeItem(item); else openItem(item);
                });
            });
        });
    }

    function buildIngredientsHtml(ingredients) {
        if (!ingredients || !ingredients.length) return '';
        return '<ul class="tw-space-y-3 tw-list-none tw-p-0 tw-m-0">' +
            ingredients.map(function (ing) {
                var label = escapeHtml(ing.title) + (ing.amount ? ' ' + escapeHtml(ing.amount) : '');
                return '<li>' +
                    '<span class="tw-font-medium tw-text-charcoal">' + label + '</span>' +
                    (ing.description ? '<p class="tw-mt-0.5 tw-text-charcoal/70 tw-m-0">' + escapeHtml(ing.description) + '</p>' : '') +
                    '</li>';
            }).join('') +
            '</ul>';
    }

    function renderSummary(product) {
        var imageUrl        = escapeHtml(product.imageUrl || '');
        var title           = escapeHtml(product.title || '');
        var displayTitle    = escapeHtml(product.displayTitle || '')
        var usageType       = product.usageType || '';

        var titleDescHtml =
            '<h3 class="tw-normal-case tw-text-charcoal tw-text-xl sm:tw-text-2xl tw-text-left md:tw-text-center">' + title + '</h3>' +
            (displayTitle ? '<div class="tw-mt-1 tw-text-base tw-text-light-charcoal tw-text-left md:tw-text-center">' + displayTitle + '</div>' : '');

        return '<div class="tw-bg-white tw-py-4">' +
            '<h3 class="tw-text-charcoal md:tw-text-center tw-text-xl sm:tw-text-2xl tw-pb-4 md:tw-pb-8">Your Treatment Plan</h3>' +
            '<div class="tw-flex tw-gap-4 md:tw-items-center">' +
                '<div class="tw-h-[100px] tw-w-[100px] tw-shrink-0 sm:tw-w-[150px] sm:tw-h-[150px] md:tw-h-[240px] md:tw-w-[240px]">' +
                    (imageUrl
                        ? '<img src="' + imageUrl + '" alt="' + title + '" class="tw-h-full tw-w-full tw-rounded-md tw-object-cover" loading="eager" />'
                        : '') +
                '</div>' +
                '<div class="tw-min-w-0 tw-flex-1">' +
                    '<div class="md:tw-hidden">' + titleDescHtml + '</div>' +
                    '<div class="tw-hidden md:tw-block">' + renderUsageChips(usageType, true) + '</div>' +
                '</div>' +
            '</div>' +
            '<div class="tw-hidden md:tw-block tw-mt-4">' + titleDescHtml + '</div>' +
        '</div>';
    }

    function renderDetails(product, variant) {
        var usageType  = product.usageType || '';
        var descriptionHtml = product.descriptionHtml || '';
        // Prefer variant-level ingredients when present (e.g. prime-daily Pro vs Core).
        var ingredients = (variant && variant.ingredients && variant.ingredients.length)
            ? variant.ingredients
            : (product.ingredients || []);
        var activeIngredientsHtml = buildIngredientsHtml(ingredients);
        var whatToKnowHtml        = product.whatToKnowHtml || '';
        var howToTakeHtml         = product.howToTakeHtml  || '';
        var hasAccordion          = activeIngredientsHtml || whatToKnowHtml || howToTakeHtml;

        return '<div class="tw-bg-white tw-pt-2">' +
            '<div class="md:tw-hidden">' + renderUsageChips(usageType) + '</div>' +
            '<div class="tw-mt-4 tw-text-sm sm:tw-text-base tw-text-light-charcoal tw-text-left md:tw-text-center">' + descriptionHtml + '</div>' +
            (hasAccordion
                ? '<div class="tw-mt-4 tw-border-t tw-border-black/10" data-brx-return-accordion>' +
                    renderAccordionItem('Active Ingredients', activeIngredientsHtml) +
                    renderAccordionItem('What to Know',       whatToKnowHtml) +
                    renderAccordionItem('How to Take',        howToTakeHtml) +
                  '</div>'
                : '') +
        '</div>';
    }

    function mount(root) {
        var returnEl  = root ? root.querySelector('[data-intake-return]')  : null;
        var overlayEl = root ? root.querySelector('[data-intake-overlay]') : null;
        var hostEl    = root ? root.querySelector('[data-intake-host]')    : null;

        if (!returnEl) return;

        // --- Surface visibility ---------------------------------------------
        if (overlayEl) { overlayEl.classList.add('is-hidden'); overlayEl.setAttribute('aria-busy', 'false'); }
        if (hostEl)    hostEl.classList.add('is-hidden');
        returnEl.classList.remove('is-hidden');
        root.classList.remove('is-warmup');

        // --- Data -----------------------------------------------------------
        var product = window.BRXSession && window.BRXSession.getSelectionProduct
            ? window.BRXSession.getSelectionProduct()
            : null;

        var variant = window.BRXSession && window.BRXSession.getSelectionVariant
            ? window.BRXSession.getSelectionVariant()
            : null;

        var resolvedCheckout = window.BRXSession && window.BRXSession.getResolvedCheckoutUrl
            ? window.BRXSession.getResolvedCheckoutUrl()
            : null;
        var checkoutUrl = resolvedCheckout && resolvedCheckout.url ? resolvedCheckout.url : null;

        // --- Render product content -----------------------------------------
        var summaryEl = returnEl.querySelector('[data-intake-return-summary]');
        var detailsEl = returnEl.querySelector('[data-intake-return-details]');

        if (product) {
            if (summaryEl) summaryEl.innerHTML = renderSummary(product);
            if (detailsEl) {
                detailsEl.innerHTML = renderDetails(product, variant);
                initAccordions(detailsEl);
            }
        }

        // --- Checkout button ------------------------------------------------
        var checkoutBtn = returnEl.querySelector('[data-intake-return-checkout]');
        if (checkoutBtn) {
            if (checkoutUrl) {
                checkoutBtn.href = checkoutUrl;
            } else {
                checkoutBtn.setAttribute('aria-disabled', 'true');
                checkoutBtn.style.opacity = '0.6';
                checkoutBtn.style.pointerEvents = 'none';
            }
        }
    }

    window.BRXCheckoutReturn = { mount: mount };
})();
